import { CellStack, FieldGrid, MetricGrid, StateBlock, StatusBadge } from "@/components/layout-2/ds";
import { ChangeLog, DenseTable, Disclosure, FilterBars, InlineAlert } from "@/components/layout-2/ds-settings";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatParis } from "@/utils/dialer-datetime";
import { formatNumber } from "@/utils/format-fr";
import { RotateCw } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { feedingClient } from "./api";
import { DetailDialog } from "./detail-dialog";
import { FilterChips, useFilterDefinitions } from "./filter-chips";
import { LinesTable } from "./lines-table";
import { passName, type RunList } from "./runs";
import { settingsItems } from "./settings-used";
import type { Counters, FeedingLine, FeedingPageProps, FeedingReason, HistoryEntry, Preview, RunDetail, RunRow } from "./types";
import { useLoad } from "./use-load";

type DetailProps = FeedingPageProps & { onClose: () => void };

/** The counters of a pass or of the preview, out of the records read. */
function Counts({ candidates, counters }: { candidates: number; counters: Counters }) {
    return (
        <div className="flex-shrink-0 border-b px-6 py-3" data-testid="headline-counters">
            <MetricGrid
                columns={4}
                metrics={[
                    { label: "Records read", value: formatNumber(candidates) },
                    { label: "Injected", value: formatNumber(counters.injected), hint: "new cards", tone: counters.injected ? "success" : undefined },
                    { label: "Ignored", value: formatNumber(counters.ignored), hint: "some are retried later" },
                    { label: "Excluded", value: formatNumber(counters.excluded), hint: "by an exclusion" }
                ]}
            />
        </div>
    );
}

/** Left column of a pass: the reasons, each filtering the records, then the explanations kept out of the way. */
function Reasons({ reasons, total, value, onChange, children }: { reasons: FeedingReason[]; total: number; value: string | null; onChange: (code: string | null) => void; children?: React.ReactNode }) {
    return (
        <div className="flex max-h-60 flex-shrink-0 flex-col gap-3 overflow-y-auto border-b p-3 lg:max-h-none lg:w-64 lg:border-b-0 lg:border-r">
            <FilterBars rows={reasons} total={total} value={value} onChange={onChange} allLabel="All records" />
            {children}
        </div>
    );
}

function ReasonOrder({ page }: { page: FeedingPageProps["page"] }) {
    return (
        <Disclosure title="When several reasons apply">
            <p className="mb-2 text-gray-600">A record shows one reason only, the first of this list, so the counters add up to the records read.</p>
            <FieldGrid columns={1} items={page.help.reason_priority.map((item, index) => ({ label: `${index + 1}. ${item.label}`, value: item.why }))} />
        </Disclosure>
    );
}

/* ------------------------------------------------------------------ */
/* Preview                                                              */
/* ------------------------------------------------------------------ */

/** What the next pass would do with the saved settings, right now. Nothing is created. */
export function PreviewDialog({ page, base, changeService, refresh, onClose }: DetailProps) {
    const navigate = useNavigate();
    const code = page.campaign.code;
    const [reason, setReason] = useState<string | null>(null);
    const definitions = useFilterDefinitions(page.source.list_view?.object.url);
    const { data, error, loading, reload } = useLoad<Preview>(
        async () => {
            const preview = await feedingClient(code, "dry-run/").post({}).json<Preview>();
            // The checklist counts a preview among the steps: read the page again.
            refresh();
            return preview;
        },
        [code],
        "The preview failed."
    );
    const clean = !!data && data.alerts.length === 0;
    const lines = data ? data.sample.filter(line => !reason || line.reason.code === reason) : [];
    const canPutInService = page.can_edit && page.state !== "active";

    return (
        <DetailDialog
            title="Preview of the next pass"
            meta={data ? `Computed on ${formatParis(data.run.started_at)} with the saved settings. Nothing is created, nothing is excluded.` : "Same rules as a real pass. Nothing is created, nothing is excluded."}
            actions={
                <Button variant="outline" size="sm" onClick={reload} disabled={loading} data-testid="preview-again">
                    <RotateCw className="mr-1 h-4 w-4" /> Run again
                </Button>
            }
            onClose={onClose}
            testId="preview-dialog"
            footer={
                <>
                    <span className="text-sm text-gray-600">{data && (!clean ? "Review the alerts before going further." : canPutInService && "The preview matches what you expect? Put the campaign in service.")}</span>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={onClose}>
                            Close
                        </Button>
                        {canPutInService && data && (
                            <Button
                                disabled={!page.summary.put_in_service.enabled}
                                onClick={async () => {
                                    if (await changeService("activate")) navigate(base);
                                }}
                                data-testid="activate-from-dry-run"
                            >
                                {page.summary.put_in_service.label}
                            </Button>
                        )}
                    </div>
                </>
            }
        >
            {loading ? (
                <StateBlock kind="loading" message={<span data-testid="dry-run-loading">Running the ListView and the rules…</span>} className="py-24" />
            ) : error || !data ? (
                <div className="p-6">
                    <InlineAlert type="error" title="The preview failed">
                        <span data-testid="dry-run-error">{error}</span>
                    </InlineAlert>
                </div>
            ) : (
                <>
                    {data.alerts.length > 0 && (
                        <div className="flex flex-shrink-0 flex-col gap-2 px-6 pt-3">
                            {data.alerts.map((alert, index) => (
                                <InlineAlert key={index} type={alert.type} title={alert.title}>
                                    {alert.text && <span data-testid="dry-run-alert">{alert.text}</span>}
                                </InlineAlert>
                            ))}
                        </div>
                    )}
                    {data.too_large ? (
                        <StateBlock message="The preview was not computed: the source ListView is over the size limit. Add filters, then run it again." className="py-24" />
                    ) : (
                        <>
                            <Counts candidates={data.candidates} counters={data.counters} />
                            <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
                                <Reasons reasons={data.reasons} total={data.candidates} value={reason} onChange={setReason}>
                                    <p className="px-1 text-xs text-gray-600">The table shows a sample of {formatNumber(data.sample.length)} records covering every reason.</p>
                                    <ReasonOrder page={page} />
                                    <Disclosure title="Settings used">
                                        <div data-testid="settings-used">
                                            <FieldGrid
                                                columns={1}
                                                items={[
                                                    { label: "ListView", value: data.settings_used.list_view_name },
                                                    { label: "Filters", value: <FilterChips filters={data.settings_used.filters} definitions={definitions} /> },
                                                    ...settingsItems(data.settings_used, page.config.fields)
                                                ]}
                                            />
                                        </div>
                                    </Disclosure>
                                </Reasons>
                                {data.candidates === 0 ? <StateBlock message="No record read: nothing would happen." className="flex-1" /> : <LinesTable lines={lines} campaignCode={code} runId={data.run.id} base={base} />}
                            </div>
                        </>
                    )}
                </>
            )}
        </DetailDialog>
    );
}

/* ------------------------------------------------------------------ */
/* Passes                                                               */
/* ------------------------------------------------------------------ */

const ALL = "all";

/** Every evaluation of the ListView, newest first; a row opens its detail in place. */
export function RunsDialog({ page, base, onClose }: DetailProps) {
    const code = page.campaign.code;
    const [runType, setRunType] = useState(ALL);
    const [pageNumber, setPageNumber] = useState(1);
    const [rows, setRows] = useState<RunRow[]>([]);
    const [types, setTypes] = useState<RunList["run_types"]>([]);
    const { data, error, loading, reload } = useLoad<RunList>(
        () => feedingClient(code, "runs/").query({ ...(runType === ALL ? {} : { run_type: runType }), page: pageNumber }).get().json<RunList>(),
        [code, runType, pageNumber],
        "Unable to load the passes."
    );
    useEffect(() => {
        if (!data) return;
        setRows(previous => (pageNumber === 1 ? data.results : [...previous, ...data.results]));
        setTypes(data.run_types ?? []);
    }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
    const count = data?.count ?? 0;

    return (
        <DetailDialog
            title="All passes"
            meta="Every evaluation of the ListView — scheduled, triggered by a record, reconciliation or preview. A record-saved or before-action check shows only when it excludes a card."
            size="lg"
            onClose={onClose}
            testId="runs-dialog"
            actions={
                <Select
                    value={runType}
                    onValueChange={value => {
                        setRunType(value);
                        setPageNumber(1);
                        setRows([]);
                    }}
                >
                    <SelectTrigger className="w-44" aria-label="Type of pass">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL}>All types</SelectItem>
                        {types.map(type => (
                            <SelectItem key={type.value} value={type.value}>
                                {type.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            }
        >
            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-6 py-4">
                {error ? (
                    <StateBlock kind="error" message={error} onRetry={reload} />
                ) : rows.length === 0 ? (
                    loading ? (
                        <StateBlock kind="loading" />
                    ) : (
                        <div data-testid="runs-empty">
                            <StateBlock message={runType === ALL ? "No pass yet — passes appear once the campaign is in service or after a preview." : "No pass of this type. Try another type."} />
                        </div>
                    )
                ) : (
                    <>
                        <DenseTable<RunRow>
                            rows={rows}
                            itemToId={run => String(run.id)}
                            primaryColumn="pass"
                            columns={[
                                {
                                    name: "Pass",
                                    key: "pass",
                                    size: 110,
                                    navigateTo: run => `${base}/runs/${run.id}`,
                                    cellFn: run => (
                                        <Link to={`${base}/runs/${run.id}`} className="text-blue-700 hover:text-blue-900 hover:underline" data-testid={`run-row-${run.id}`}>
                                            {passName(run)}
                                        </Link>
                                    )
                                },
                                { name: "When", key: "when", size: 140, cellFn: run => <span className="tabular-nums">{formatParis(run.started_at)}</span> },
                                { name: "Type", key: "type", size: 120, cellFn: run => <StatusBadge value={run.run_type} /> },
                                { name: "Trigger", key: "trigger", size: 120, cellFn: run => run.trigger.label },
                                { name: "Injected", key: "injected", size: 80, cellClassName: "text-right", cellFn: run => <span className="tabular-nums">{formatNumber(run.counters.injected)}</span> },
                                { name: "Ignored", key: "ignored", size: 80, cellClassName: "text-right", cellFn: run => <span className="tabular-nums">{formatNumber(run.counters.ignored)}</span> },
                                { name: "Excluded", key: "excluded", size: 80, cellClassName: "text-right", cellFn: run => <span className="tabular-nums">{formatNumber(run.counters.excluded)}</span> },
                                { name: "Status", key: "status", size: 110, cellFn: run => <StatusBadge value={run.status} /> }
                            ]}
                        />
                        {rows.length < count && (
                            <Button variant="outline" size="sm" className="self-start" disabled={loading} onClick={() => setPageNumber(value => value + 1)}>
                                Load more passes
                            </Button>
                        )}
                    </>
                )}
            </div>
        </DetailDialog>
    );
}

const seconds = (run: RunRow) => (run.finished_at ? Math.max(0, Math.round((Date.parse(run.finished_at) - Date.parse(run.started_at)) / 1000)) : null);

/** One pass: its counters, the records by reason with their "Why?", and the settings it ran with. */
export function RunDetailDialog({ page, base, runId, onClose }: DetailProps & { runId: string }) {
    const code = page.campaign.code;
    const definitions = useFilterDefinitions(page.source.list_view?.object.url);
    const [reason, setReason] = useState<string | null>(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [shown, setShown] = useState<RunDetail | null>(null);
    const [lines, setLines] = useState<FeedingLine[]>([]);
    const { data, error, loading, reload } = useLoad<RunDetail>(
        () => feedingClient(code, `runs/${runId}/`).query({ ...(reason ? { reason } : {}), page: pageNumber }).get().json<RunDetail>(),
        [code, runId, reason, pageNumber],
        "This pass could not be loaded. It may be older than the retention period."
    );
    useEffect(() => {
        if (!data) return;
        setShown(data);
        setLines(previous => (pageNumber === 1 ? (data.lines?.results ?? []) : [...previous, ...(data.lines?.results ?? [])]));
    }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

    const back = { label: "All passes", to: `${base}/runs` };
    if (!shown) {
        return (
            <DetailDialog title="Pass" back={back} onClose={onClose} testId="run-dialog">
                <div className="p-6">
                    {error ? (
                        <InlineAlert type="error" title="Pass not found">
                            <span data-testid="run-not-found">{error}</span>
                        </InlineAlert>
                    ) : (
                        <StateBlock kind="loading" />
                    )}
                </div>
            </DetailDialog>
        );
    }

    const { run, today } = shown;
    const failed = !!run.error;
    const duration = seconds(run);
    const settings = (
        <Disclosure title="Settings of this pass">
            <div className="flex flex-col gap-3">
                <div data-testid="config-used">
                    <FieldGrid
                        columns={1}
                        items={[
                            ...settingsItems(shown.config_used, page.config.fields),
                            { label: "Today", value: today.config_changed ? <Badge tone="warning">Differs</Badge> : <Badge tone="neutral">Same as today</Badge> }
                        ]}
                    />
                </div>
                <div data-testid="filters-snapshot">
                    <FieldGrid
                        columns={1}
                        items={[
                            ...shown.filters_executed.map(entry => ({
                                label: entry.role === "source" ? "ListView" : "Exclusion",
                                value: (
                                    <div className="flex flex-col gap-1">
                                        <span>{entry.listview}</span>
                                        <FilterChips filters={entry.filters} definitions={definitions} />
                                    </div>
                                )
                            })),
                            {
                                label: "Today",
                                value: today.filters_changed ? <CellStack primary={<Badge tone="warning">Edited since</Badge>} secondary="See the change history." /> : <Badge tone="neutral">Same as today</Badge>
                            }
                        ]}
                    />
                </div>
            </div>
        </Disclosure>
    );

    return (
        <DetailDialog
            title={run.pass_number ? `Pass n°${run.pass_number}` : run.run_type.label}
            badge={<StatusBadge value={run.run_type} />}
            meta={`${formatParis(run.started_at)}${duration === null ? "" : ` · ${duration} s`} · trigger: ${run.trigger.label}`}
            back={back}
            onClose={onClose}
            testId="run-dialog"
        >
            {(error || shown.alerts.length > 0) && (
                <div className="flex flex-shrink-0 flex-col gap-2 px-6 pt-3">
                    {error && (
                        <InlineAlert
                            type="error"
                            title="The records could not be loaded"
                            action={
                                <Button variant="outline" size="sm" onClick={reload}>
                                    Retry
                                </Button>
                            }
                        >
                            {error}
                        </InlineAlert>
                    )}
                    {shown.alerts.map((alert, index) => (
                        <InlineAlert key={index} type={alert.type} title={alert.title}>
                            {alert.text && <span data-testid={failed ? "run-failed" : "run-alert"}>{alert.text}</span>}
                        </InlineAlert>
                    ))}
                </div>
            )}
            {failed ? (
                <div className="overflow-y-auto px-6 py-4">{settings}</div>
            ) : (
                <>
                    <Counts candidates={run.candidates} counters={run.counters} />
                    <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
                        <Reasons
                            reasons={shown.reasons}
                            total={run.candidates}
                            value={reason}
                            onChange={value => {
                                setReason(value);
                                setPageNumber(1);
                            }}
                        >
                            <ReasonOrder page={page} />
                            {settings}
                        </Reasons>
                        {shown.purged ? (
                            <div className="flex-1" data-testid="run-purged">
                                <StateBlock message={`The per-record detail of this pass was purged after ${shown.retention_days} days. Its counters and settings are kept.`} />
                            </div>
                        ) : (
                            <LinesTable
                                lines={lines}
                                campaignCode={code}
                                runId={run.id}
                                base={base}
                                footer={
                                    lines.length < (shown.lines?.count ?? 0) && (
                                        <Button variant="outline" size="sm" className="m-2 self-start" disabled={loading} onClick={() => setPageNumber(value => value + 1)}>
                                            Load more records
                                        </Button>
                                    )
                                }
                            />
                        )}
                    </div>
                </>
            )}
        </DetailDialog>
    );
}

/* ------------------------------------------------------------------ */
/* History                                                              */
/* ------------------------------------------------------------------ */

/** Who changed what, when, with a before / after table (filters included). */
export function HistoryDialog({ page, onClose }: DetailProps) {
    const { data, error, loading, reload } = useLoad(() => feedingClient(page.campaign.code, "history/").get().json<{ results: HistoryEntry[] }>(), [page.campaign.code], "Unable to load the change history.");
    return (
        <DetailDialog title="Change history" meta="Every change to the source, the filters, the rules and the exclusions. Each pass keeps its own snapshot." size="lg" onClose={onClose} testId="history-dialog">
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
                {error || loading ? (
                    <StateBlock kind={error ? "error" : "loading"} message={error ?? undefined} onRetry={reload} />
                ) : data?.results.length === 0 ? (
                    <div data-testid="history-empty">
                        <StateBlock message="No change recorded — the first save of the configuration will appear here." />
                    </div>
                ) : (
                    <ChangeLog entries={(data?.results ?? []).map(entry => ({ ...entry, date: formatParis(entry.date) }))} defaultOpen={1} />
                )}
            </div>
        </DetailDialog>
    );
}
